import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { BeatLoader, BounceLoader } from "react-spinners";
import { useReactToPrint } from "react-to-print";
import * as yup from "yup";
import excel from "../../../assets/images/img-icons/excel.png";
import pdf from "../../../assets/images/img-icons/pdf.png";
import CustomerType from "../../../components/modals/customers/create/CustomerType";
import PaginationLinks from "../../../components/pagination/PaginationLinks";
import PageTitle from "../../../components/partials/PageTitle";
import {
  FilteredCorperateCustomers,
  getCorperateCustomers,
  getCorperateCustomersSearch,
  resetCustomerStatus,
  resetSingleCustomer,
} from "../../../redux/slice/customer/customerSlice";
import { sortUsersByOffice } from "../../../utils/helpers/functions";
import Select from "./../../../components/form/select/Select";

// main component starts here
const CorporateCustomerTable = () => {
  // redux state calls
  const { user_permissions } = useSelector((state) => state?.auth?.user);
  const { user } = useSelector((state) => state?.auth?.user);
  const { all_users } = useSelector((state) => state?.user);
  const {
    offices: { offices },
  } = useSelector((state) => state.office);
  const { customers, isSuccess, isFiltered, isFiltering, isSearching } =
    useSelector((state) => state?.customer);
  const [currentCustomers, setCurrentCustomers] = useState(
    customers?.corperate?.data
  );

  const [createCorporateCustomer, setCreateCorporateCustomer] = useState();
  const [createIndividualCustomer, setCreateIndividualCustomer] = useState();
  const [hasSeeCustomer, setHasSeeCustomer] = useState();
  const [viewCustomer, setViewCustomer] = useState();

  // local states and refs
  const [sortedUsers, setSortedUsers] = useState([]);
  const [filtered, setFiltered] = useState("");
  const [filteredItem, setFilteredItem] = useState(false);
  const [searchBy, setSearchBy] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const agentCusRef = useRef();
  const allCorperateCustomers = useRef();
  const corperateOffFilter = useRef();
  const searchCorp = useRef();
  const CCPdfRef = useRef();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // print table to pdf
  const generatePDF = useReactToPrint({
    content: () => CCPdfRef.current,
    documentTitle: "corperate customers",
  });

  // export data to excel file
  const exportXlsxData = () => {
    const itemsWithValues = {};
    return currentCustomers?.map((item) => {
      for (const key in item) {
        if (item[key]) {
          let keyChange = key?.split("_")?.join(" ");
          itemsWithValues[keyChange] = item[key];
        }
      }
      return itemsWithValues;
    });
  };

  // breadcrumb
  const breadcrumb = {
    from: {
      title: "Customer",
      link: "/admin/corporate-customers",
    },
    current: {
      title: "overview",
      link: null,
    },
  };

  const handleSearchBy = (e) => {
    e.preventDefault();
    if (searchBy) {
      dispatch(
        getCorperateCustomersSearch({ searchBy, office_id: user?.office_id })
      );
    }
  };

  // fetch customers on click of pagination links
  const fetchCorperateCustomersHandler = async (pageNum) => {
    setPageNumber(pageNum);
    dispatch(
      getCorperateCustomers({ pageNumber: pageNum, office_id: user?.office_id })
    );
    dispatch(resetCustomerStatus());
  };

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldError,
    resetForm,
  } = useFormik({
    // enableReinitialize: true,
    initialValues: {
      office_id: "",
      staff_id: "",
    },
    validationSchema: () =>
      yup.object().shape({
        office_id: yup.number().required("Office is required"),
      }),
    onSubmit: (values) => {
      if (values?.staff_id === undefined) {
        values["staff_id"] = "";
      }
      dispatch(resetCustomerStatus());
      dispatch(FilteredCorperateCustomers(values));
      // setFiltered("");
    },
  });

  const clearFilteredItems = () => {
    corperateOffFilter?.current?.clearValue();
    // agentCusRef?.current?.clearValue();
    setSearchBy("");
    dispatch(
      getCorperateCustomers({
        pageNumber: pageNumber,
        office_id: user?.office_id,
      })
    );
    resetForm();
    setFilteredItem(false);
  };

  // get customers on first load
  useEffect(() => {
    dispatch(
      getCorperateCustomers({ pageNumber: 1, office_id: user?.office_id })
    );
    // dispatch(resetCustomerStatus());
  }, [dispatch, user?.office_id]);

  useEffect(() => {
    if (customers) {
      setCurrentCustomers(customers?.corperate?.data);
    }
  }, [customers]);

  useEffect(() => {
    agentCusRef?.current?.clearValue();
    if (values?.office_id) {
      let sortedUsers = sortUsersByOffice(values?.office_id, all_users?.data);
      setSortedUsers(sortedUsers);
    } else {
      setSortedUsers([]);
    }
  }, [values?.office_id, all_users?.data]);

  useEffect(() => {
    if (isSuccess) {
      allCorperateCustomers?.current?.click();
      setFiltered("");
      dispatch(resetCustomerStatus());
    }
    if (isFiltered) {
      setFilteredItem(true);
    }

    if (filtered !== "") {
      setCurrentCustomers(
        customers?.corperate?.data?.filter((item) => item.status === filtered)
      );
    } else {
      setCurrentCustomers(customers?.corperate?.data);
    }
  }, [filtered, isFiltered, isSuccess]);

  useEffect(() => {
    if (
      !user_permissions?.some(
        (permission) => permission.name === "customer module"
      ) ||
      !user_permissions?.some(
        (permission) => permission.name === "view customers - corporate"
      )
    ) {
      navigate("/admin/dashboard");
    }
    setCreateIndividualCustomer(
      user_permissions?.some(
        (permission) => permission.name === "create customers - individual"
      )
    );
    setCreateCorporateCustomer(
      user_permissions?.some(
        (permission) => permission.name === "create customer - corporate"
      )
    );
    setViewCustomer(
      user_permissions?.some(
        (permission) => permission.name === "view customer details"
      )
    );
    setHasSeeCustomer(
      user_permissions?.some(
        (permission) => permission.name === "see customers"
      )
    );
  }, [navigate, user_permissions]);

  return (
    <>
      {/* page title */}
      <PageTitle title='Corporate Customers' breadcrumb={breadcrumb} />
      {/* end page title */}

      {/* Table row */}

      {!customers?.corperate && (
        <BeatLoader
          height='80'
          width='80'
          radius='9'
          color='#2e3192'
          ariaLabel='three-dots-loading'
          wrapperStyle={{}}
          wrapperClassName=''
          visible={true}
        />
      )}

      {customers?.corperate && (
        <>
          <div className='row mt- card p-1'>
            <div className='row mb-3'>
              {hasSeeCustomer && <form className='col-md-10' onSubmit={handleSubmit}>
                <div className='row mt-4'>
                  <div className='col-sm-2 text-end sm-text-end'>
                    <label htmlFor='account_type' className={`form-label mt-1`}>
                      Office <span className='text-danger'>*</span>
                    </label>
                  </div>

                  <div className='col-sm-6'>
                    <Select
                      onBlur={() => {
                        handleBlur({ target: { name: "office_id" } });
                      }}
                      name='office_id'
                      data={offices?.map((item, i) => ({
                        label: item?.office_name,
                        value: item?.id,
                      }))}
                      // value={parseInt(values?.office_id)}
                      onChange={(selectedOption) => {
                        setFieldValue("office_id", selectedOption?.value);
                      }}
                      ref={corperateOffFilter}
                    />
                    {errors?.office_id && touched?.office_id && (
                      <p className='mb-0 text-danger'>
                        {errors?.office_id + "*"}
                      </p>
                    )}
                  </div>
                </div>
                <div className='row mt-2'>
                  <div className='col-sm-2 text-end sm-text-end'>
                    <label htmlFor='account_type' className={`form-label mt-1`}>
                      Agent
                    </label>
                  </div>
                  <div className='col-sm-6'>
                    <Select
                      onBlur={() => {
                        handleBlur({ target: { name: "staff_id" } });
                      }}
                      name='staff_id'
                      ref={agentCusRef}
                      data={
                        values.office_id === 1
                          ? all_users?.data?.map((item) => ({
                              label: item?.last_name + " " + item?.first_name,
                              value: item?.staff_id,
                            }))
                          : sortedUsers
                      }
                      onChange={(selectedOption) => {
                        setFieldValue("staff_id", selectedOption?.value);
                        setFieldError("staff_id", "");
                      }}
                    />
                    <div className='d-flex align-items-center gap-2 mt-2'>
                      <button type='submit' className='btn btn-sm btn-primary '>
                        Filter
                      </button>
                      {filteredItem ? (
                        <button
                          type='button'
                          className='btn btn-sm btn-secondary'
                          onClick={clearFilteredItems}
                        >
                          Clear Filter
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </form>}
              {(createCorporateCustomer || createIndividualCustomer) && (
                <div className='col-md-2 pt-3'>
                  <div className='d-flex justify-content-end'>
                    <button
                      className='btn btn-sm btn-primary mt-2 m-0'
                      data-bs-toggle='modal'
                      data-bs-target='#create_customer_step_1'
                    >
                      Add Customer
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className='d-flex justify-content-between py-2'>
              {/* <h4 className='text-primary-1'>All Customers</h4> */}
              <div>
                <ul class='nav nav-tabs nav-bordered mb-'>
                  <li class='nav-item' onClick={() => setFiltered("")}>
                    <a
                      href='#home-b1'
                      data-bs-toggle='tab'
                      aria-expanded='false'
                      class='nav-link active'
                      ref={allCorperateCustomers}
                    >
                      <i class='mdi mdi-home-variant d-md-none d-block'></i>
                      <span class='d-none d-md-block'>All</span>
                    </a>
                  </li>
                  <li class='nav-item' onClick={() => setFiltered("2")}>
                    <a
                      href='#profile-b1'
                      data-bs-toggle='tab'
                      aria-expanded='true'
                      class='nav-link '
                    >
                      <i class='mdi mdi-account-circle d-md-none d-block'></i>
                      <span class='d-none d-md-block'>Active</span>
                    </a>
                  </li>
                  <li class='nav-item' onClick={() => setFiltered("1")}>
                    <a
                      href='#settings-b1'
                      data-bs-toggle='tab'
                      aria-expanded='false'
                      class='nav-link'
                    >
                      <i class='mdi mdi-settings-outline d-md-none d-block'></i>
                      <span class='d-none d-md-block'>Pending Approval</span>
                    </a>
                  </li>
                  <li class='nav-item' onClick={() => setFiltered("3")}>
                    <a
                      href='#settings-b1'
                      data-bs-toggle='tab'
                      aria-expanded='false'
                      class='nav-link'
                    >
                      <i class='mdi mdi-settings-outline d-md-none d-block'></i>
                      <span class='d-none d-md-block'>Rejected</span>
                    </a>
                  </li>
                  <li class='nav-item' onClick={() => setFiltered("4")}>
                    <a
                      href='#settings-b1'
                      data-bs-toggle='tab'
                      aria-expanded='false'
                      class='nav-link'
                    >
                      <i class='mdi mdi-settings-outline d-md-none d-block'></i>
                      <span class='d-none d-md-block'>Closed</span>
                    </a>
                  </li>
                </ul>
              </div>
              <div className='d-flex justify-content-between align-items-center gap-3'>
                <form
                  onSubmit={handleSearchBy}
                  className='d-flex bg-primary-1 align-items-center '
                  style={{ height: "30px", width: "217px" }}
                  ref={searchCorp}
                >
                  <input
                    type='search'
                    value={searchBy}
                    className='border-0'
                    placeholder='Search...'
                    onChange={(e) => setSearchBy(e.target.value)}
                    style={{
                      height: "28px",
                      marginLeft: "1px",
                      outline: "none",
                    }}
                  />
                  <button
                    type='submit'
                    className='btn btn-primary btn-sm d-flex align-items-center gap-1'
                    style={{ height: "28px" }}
                  >
                    <i className='uil uil-search text-white fs-4'></i>
                  </button>
                </form>
                <div className=' d-flex align-items-center border '>
                  <span
                    className='bg-info me-2 text-white'
                    style={{ padding: "3px" }}
                  >
                    Export to
                  </span>

                  <span
                    href='#'
                    id='btnExport'
                    className='me-1 cursor-p'
                    title='Export to Pdf'
                    onClick={generatePDF}
                  >
                    <img
                      src={pdf}
                      alt='export-pdf'
                      style={{ height: "20px" }}
                    />
                  </span>
                  {currentCustomers ? (
                    <CSVLink
                      data={exportXlsxData()}
                      style={{ fontWeight: "bolder", cursor: "pointer" }}
                      filename='Corperate customers'
                    >
                      <span
                        href='#'
                        id='btnExport'
                        className='mr-1'
                        title='Export to Excel'
                      >
                        <img
                          src={excel}
                          alt='export-excel'
                          style={{ height: "20px", marginRight: "5px" }}
                        />
                      </span>
                    </CSVLink>
                  ) : null}
                </div>
              </div>
            </div>
            <div className='col-12'>
              <div className='position-relative'>
                <div className='row'>
                  <div className='col-sm-12'>
                    <div className='table-responsive'>
                      <table
                        className='table table-centered table-striped nowrap w-100 no-footer'
                        ref={CCPdfRef}
                      >
                        <thead>
                          <tr>
                            <th
                              className='sorting'
                              tabIndex='0'
                              rowSpan='1'
                              colSpan='1'
                              style={{ width: "130.8px" }}
                              aria-label='Customer: activate to sort column ascending'
                            >
                              Status
                            </th>
                            <th
                              className='sorting'
                              tabIndex='0'
                              rowSpan='1'
                              colSpan='1'
                              style={{ width: "225.8px" }}
                              aria-label='Customer: activate to sort column ascending'
                            >
                              Business Name
                            </th>
                            <th
                              className='sorting'
                              tabIndex='0'
                              rowSpan='1'
                              colSpan='1'
                              style={{ width: "167.8px" }}
                            >
                              Customer Type
                            </th>
                            <th
                              className='sorting'
                              tabIndex='0'
                              rowSpan='1'
                              colSpan='1'
                              style={{ width: "167.8px" }}
                              aria-label='Email: activate to sort column ascending'
                            >
                              Customer Id
                            </th>
                            <th
                              className='sorting'
                              tabIndex='0'
                              rowSpan='1'
                              colSpan='1'
                              style={{ width: "206.8px" }}
                              aria-label='Location: activate to sort column ascending'
                            >
                              Town
                            </th>
                            <th
                              className='sorting sorting_asc'
                              tabIndex='0'
                              rowSpan='1'
                              colSpan='1'
                              style={{ width: "166.8px" }}
                            >
                              Phone Number
                            </th>
                            <th
                              className='sorting'
                              tabIndex='0'
                              rowSpan='1'
                              colSpan='1'
                              style={{ width: "165.8px" }}
                              aria-label='Status: activate to sort column ascending'
                            >
                              Date Registered
                            </th>
                            {viewCustomer && (
                              <th
                                tabIndex='0'
                                rowSpan='1'
                                colSpan='1'
                                style={{ width: "100.8px" }}
                              ></th>
                            )}
                          </tr>
                        </thead>
                        {currentCustomers?.length ? (
                          <tbody>
                            {currentCustomers?.map((item, i) => (
                              <tr>
                                <td>
                                  <span
                                    className={`badge ${
                                      item?.status === "1" &&
                                      "badge-warning-lighten"
                                    } ${
                                      item?.status === "2" &&
                                      "badge-success-lighten"
                                    } ${
                                      item?.status === "3" &&
                                      "badge-danger-lighten"
                                    } ${
                                      item?.status === "4" &&
                                      "badge-secondary-lighten"
                                    } fw-normal rounded-4`}
                                    style={{
                                      fontSize: "10px",
                                      padding: "5px 18px",
                                    }}
                                  >
                                    {item?.status === "1" && "Pending Approval"}
                                    {item?.status === "2" && "Active"}
                                    {item?.status === "3" && "Rejected"}
                                    {item?.status === "4" && "Closed"}
                                  </span>
                                </td>
                                <td>{item?.business_name}</td>
                                <td className='table-user'>
                                  {item?.customer_type}
                                </td>
                                <td className='text-dark'>
                                  {item?.customer_id}
                                </td>
                                <td>{item?.town}</td>
                                <td>{item?.phone_number}</td>
                                <td>
                                  {moment(item?.created_at).format("D/M/Y")}
                                </td>

                                {viewCustomer && (
                                  <td className='text-center'>
                                    <Link
                                      to={`/admin/customer-profile/${item?.id}`}
                                      className='bg-primary-1 text-white px-2 py-1 rounded'
                                      state={item?.id}
                                    >
                                      view
                                    </Link>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        ) : null}
                      </table>
                    </div>
                    {currentCustomers?.length < 1 ? (
                      <h5 className='alert alert-info'>No result found!</h5>
                    ) : null}
                    <div className='row mt-2'>
                      <div className='col-sm-12 col-md-6'>
                        <div
                          className='dataTables_info table-pages'
                          id='products-datatable_info'
                          role='status'
                          aria-live='polite'
                        >
                          Showing offices {customers?.corperate?.from} to{" "}
                          {customers?.corperate?.to} of{" "}
                          {customers?.corperate?.total}
                        </div>
                      </div>
                      <div className='col-sm-12 col-md-6'>
                        <PaginationLinks
                          paginationData={customers?.corperate}
                          getNextPage={fetchCorperateCustomersHandler}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {(isFiltering || isSearching) && (
                  <div
                    className='position-absolute top-0 start-0 end-0 bottom d-flex justify-content-center align-items-center transparent-primary z-1000 '
                    style={{ height: "100%" }}
                  >
                    <div className='mx-auto'>
                      <BounceLoader
                        color='#090b8f'
                        loading={true}
                        // cssOverride={override}
                        size={50}
                        aria-label='Loading Spinner'
                        data-testid='loader'
                        className='ms-3'
                      />
                      <span className='fw-bolder text-primary-1 ms-2'>
                        Please wait...
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <CustomerType modal_id='create_customer_step_1' />
          </div>
        </>
      )}
    </>
  );
};

export default CorporateCustomerTable;
